import { db } from "@/main";
import { IresDeviceAssignment } from "../models/IresDeviceAssignment";
import { getMemberClientIds } from "./ClientRepository";
import { getIresDeviceRef } from "./IresDeviceRepository";

export async function getAllAssignmentsOfMemberClients(
  serialNumber: string
): Promise<IresDeviceAssignment[]> {
  const memberClientIds = getMemberClientIds();

  return (
    await db
      .collection(getIresDeviceRef(serialNumber) + "/assignments")
      .orderBy("queryEndDate", "desc")
      .get()
  )
  .docs
  .map((doc) => doc.data() as IresDeviceAssignment)
  .filter((assignment) => memberClientIds.includes(assignment.clientId));
}

export async function getAllAssignmentsOfCurrentClient(
  serialNumber: string,
  clientId: string
): Promise<IresDeviceAssignment[]> {
  return (
    await db
      .collection(getIresDeviceRef(serialNumber) + "/assignments")
      .where("clientId", "==", clientId)
      .orderBy("queryEndDate", "desc")
      .orderBy("startDate", "desc")
      .get()
  )
  .docs
  .map((doc) => doc.data() as IresDeviceAssignment);
}

/**
 * Assignment history per device, keyed by device **and** the scope it was read for.
 *
 * The hyDry device list needs a device's assignments to decide which of its drying
 * chains belong to this client, one query per row. Rows are created and destroyed
 * repeatedly while scrolling, and the answer only changes when a device is moved
 * between clients, so it is worth remembering for the session.
 *
 * In-flight promises are cached too, so two rows for the same device cannot fire the
 * same query twice.
 */
const assignmentsByScope = new Map<string, Promise<IresDeviceAssignment[]>>();

function getCachedAssignments(
  cacheKey: string,
  load: () => Promise<IresDeviceAssignment[]>
) {
  const cached = assignmentsByScope.get(cacheKey);

  if (cached) {
    return cached;
  }

  const loading = load().catch((error) => {
    // A failed read must not be remembered as "this device has no assignments".
    assignmentsByScope.delete(cacheKey);
    throw error;
  });

  assignmentsByScope.set(cacheKey, loading);

  return loading;
}

export function getCachedAssignmentsOfMemberClients(serialNumber: string) {
  return getCachedAssignments(`members:${serialNumber}`, () =>
    getAllAssignmentsOfMemberClients(serialNumber)
  );
}

export function getCachedAssignmentsOfCurrentClient(
  serialNumber: string,
  clientId: string
) {
  return getCachedAssignments(`${clientId}:${serialNumber}`, () =>
    getAllAssignmentsOfCurrentClient(serialNumber, clientId)
  );
}

/**
 * Drops the cached assignment history. Must be called when the signed-in client
 * changes or on sign-out: a device's assignments are exactly what changes when it
 * moves between clients.
 */
export function clearCachedIresDeviceAssignments() {
  assignmentsByScope.clear();
}